import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FireIcon, ClipboardDocumentListIcon, BeakerIcon, ChartBarIcon, CalendarIcon, UsersIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { toGasDayLabel, toGasYearLabel } from '../utils/gasCalendar';
import { gasPodService, gasContractService, gasMeterReadingService, gasInvoiceService, gasNominationService } from '../services/gasService';
import { GasInvoice } from '../types/gas';
import { useLanguage } from '../contexts/LanguageContext';

interface DashboardStats {
  podCount: number;
  contractCount: number;
  activeContractCount: number;
  readingCount: number;
  invoiceCount: number;
  totalInvoiceGross: number;
  totalInvoiceGrossHuf: number;
  totalInvoiceGrossEur: number;
  nominationCount: number;
  customerCount: number;
}

interface ExpiringContract {
  id: string;
  contractNumber?: string;
  endDate: string;
  daysRemaining: number;
  customerId: string;
}

const GasDashboard: React.FC = () => {
  const { t } = useLanguage();
  const [stats, setStats] = useState<DashboardStats>({
    podCount: 0,
    contractCount: 0,
    activeContractCount: 0,
    readingCount: 0,
    invoiceCount: 0,
    totalInvoiceGross: 0,
    totalInvoiceGrossHuf: 0,
    totalInvoiceGrossEur: 0,
    nominationCount: 0,
    customerCount: 0
  });
  const [expiringContracts, setExpiringContracts] = useState<ExpiringContract[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const [pods, contracts, readings, invoices, nominations] = await Promise.all([
          gasPodService.getAllGasPods(),
          gasContractService.getAllGasContracts(),
          gasMeterReadingService.getAllGasMeterReadings ? gasMeterReadingService.getAllGasMeterReadings() : Promise.resolve([]),
          gasInvoiceService.getAllGasInvoices(),
          gasNominationService.getAllGasNominations()
        ]);

        const customerIds = new Set<string>();
        pods.forEach(p => customerIds.add(p.customerId));
        contracts.forEach(c => customerIds.add(c.customerId));

        const totalInvoiceGrossHuf = invoices
          .filter((i: GasInvoice) => i.currency === 'HUF')
          .reduce((sum, i) => sum + i.totalGross, 0);
        const totalInvoiceGrossEur = invoices
          .filter((i: GasInvoice) => i.currency === 'EUR')
          .reduce((sum, i) => sum + i.totalGross, 0);

        const today = new Date();
        const thirtyDaysFromNow = new Date(today);
        thirtyDaysFromNow.setDate(today.getDate() + 30);

        const expiring = contracts
          .filter(c => c.status === 'active' && c.endDate)
          .map(c => {
            const endDate = new Date(c.endDate!);
            const daysRemaining = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            return { id: c.id, contractNumber: c.contractNumber, endDate: c.endDate!, daysRemaining, customerId: c.customerId };
          })
          .filter(c => c.daysRemaining >= 0 && c.daysRemaining <= 30)
          .sort((a, b) => a.daysRemaining - b.daysRemaining);

        setExpiringContracts(expiring);

        setStats({
          podCount: pods.length,
          contractCount: contracts.length,
          activeContractCount: contracts.filter(c => c.status === 'active').length,
          readingCount: readings.length,
          invoiceCount: invoices.length,
          totalInvoiceGross: invoices.reduce((sum, i) => sum + i.totalGross, 0),
          totalInvoiceGrossHuf,
          totalInvoiceGrossEur,
          nominationCount: nominations.length,
          customerCount: customerIds.size
        });
      } catch (error) {
        console.error('Error loading gas dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  const StatCard: React.FC<{ title: string; value: string | number; subtitle?: string; icon: React.ReactNode; colorClass: string; to?: string }> =
    ({ title, value, subtitle, icon, colorClass, to }) => {
      const content = (
        <>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${colorClass}`}>
              {icon}
            </div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900">{loading ? '...' : value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </>
      );

      const className = "block bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow";

      if (to) {
        return <Link to={to} className={className}>{content}</Link>;
      }
      return <div className={className}>{content}</div>;
    };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('gas.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('gas.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title={t('gas.nav.customers')}
          value={stats.customerCount}
          subtitle={t('gas.dashboard.gasCustomerSubtitle')}
          icon={<UsersIcon className="h-6 w-6 text-orange-600" />}
          colorClass="bg-orange-100"
        />

        <StatCard
          title={t('gas.nav.pods')}
          value={stats.podCount}
          subtitle={t('gas.dashboard.podsSubtitle')}
          icon={<FireIcon className="h-6 w-6 text-orange-600" />}
          colorClass="bg-orange-100"
          to="/gas-pods"
        />

        <StatCard
          title={t('gas.nav.contracts')}
          value={stats.contractCount}
          subtitle={`${stats.activeContractCount} ${t('gas.contract.statuses.active').toLowerCase()}`}
          icon={<ClipboardDocumentListIcon className="h-6 w-6 text-blue-600" />}
          colorClass="bg-blue-100"
          to="/gas-contracts"
        />

        <StatCard
          title={t('gas.nav.readings')}
          value={stats.readingCount}
          subtitle={t('gas.dashboard.readingsSubtitle')}
          icon={<BeakerIcon className="h-6 w-6 text-green-600" />}
          colorClass="bg-green-100"
          to="/gas-readings"
        />

        <StatCard
          title={t('gas.nav.invoices')}
          value={stats.invoiceCount}
          subtitle={`${t('gas.invoice.totalGross')}: ${stats.totalInvoiceGrossHuf.toLocaleString('hu-HU')} HUF + ${stats.totalInvoiceGrossEur.toLocaleString('hu-HU')} EUR`}
          icon={<ChartBarIcon className="h-6 w-6 text-purple-600" />}
          colorClass="bg-purple-100"
          to="/gas-invoices"
        />

        <StatCard
          title={t('gas.nav.nominations')}
          value={stats.nominationCount}
          subtitle={t('gas.dashboard.nominationsSubtitle')}
          icon={<PaperAirplaneIcon className="h-6 w-6 text-indigo-600" />}
          colorClass="bg-indigo-100"
          to="/gas-nominations"
        />

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <CalendarIcon className="h-6 w-6 text-teal-600" />
            </div>
            <h3 className="font-semibold text-gray-900">{t('gas.calendar.title')}</h3>
          </div>
          <div className="text-sm text-gray-600 space-y-1">
            <p><span className="font-medium">{t('gas.calendar.currentGasDay')}:</span> {toGasDayLabel()}</p>
            <p><span className="font-medium">{t('gas.calendar.currentGasYear')}:</span> {toGasYearLabel()}</p>
          </div>
        </div>
      </div>

      {expiringContracts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <ClipboardDocumentListIcon className="h-4 w-4" />
            {t('gas.alerts.expiringContracts')} ({expiringContracts.length})
          </h3>
          <div className="space-y-1">
            {expiringContracts.slice(0, 5).map(contract => (
              <div key={contract.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">
                  {contract.contractNumber || t('gas.contract.unnamed')} — {t('gas.contract.endDate')}: {contract.endDate}
                </span>
                <span className={`font-medium ${
                  contract.daysRemaining <= 7 ? 'text-red-600' : 'text-amber-700'
                }`}>
                  {contract.daysRemaining === 0 ? t('gas.alerts.expiresToday') : `${contract.daysRemaining} ${t('gas.alerts.daysRemaining')}`}
                </span>
              </div>
            ))}
            {expiringContracts.length > 5 && (
              <p className="text-xs text-amber-700 mt-2">+ {expiringContracts.length - 5} {t('gas.alerts.moreContracts')}</p>
            )}
          </div>
        </div>
      )}

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-emerald-900 mb-1">{t('gas.dashboard.activeTitle')}</h3>
        <p className="text-sm text-emerald-800">
          {t('gas.dashboard.activeDescription')}
        </p>
      </div>
    </div>
  );
};

export default GasDashboard;
